/* eslint-disable max-lines */
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Chip, IconButton, InputBase, Paper, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Search } from '@mui/icons-material';
import { compact, isEmpty, values } from 'lodash';

import useInfoDialog from 'common/hooks/useInfoDialog';
import useRoles from 'common/hooks/useRoles';
import { casingFormat, formatEnumLabel } from 'common/utils';
import {
  resetShipmentFilter,
  updateShipmentFilter,
} from '../../../store/thunks/shipmentModuleThunks';
import FacilityFilterStatusChip from '../../WorkQueue/components/FacilityFilterStatusChip';
import ShipmentsFilterButton from './ShipmentsFilterButton';
import CreateManualShipmentDialog from './CreateManualShipmentDialog';

const ShipmentListToolbar = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { filter, count } = useSelector(({ shipmentModule }) => shipmentModule.list);
  const { InfoDialog, showInfoDialog } = useInfoDialog();
  const { createManualShipment } = useRoles();
  const [showCreateManualShipment, setShowCreateManualShipment] = useState(false);

  const partners = useSelector(
    ({ auth, partner: partnerStore }) => partnerStore[auth.currentTenantId]
  );
  const partnerOptions =
    partners?.map(({ partnerName, partnerUuid }) => ({
      value: partnerUuid,
      label: casingFormat(partnerName),
    })) ?? [];

  // Build practice options from partners for display
  const practiceLabels = {};
  if (filter?.partnerIds && filter?.practiceIds) {
    const selectedPartners = partners?.filter((p) => filter.partnerIds.includes(p.partnerUuid));
    selectedPartners?.forEach((partner) => {
      partner.partnerPractices?.forEach((practice) => {
        if (filter.practiceIds.includes(practice.practiceId)) {
          practiceLabels[practice.practiceId] = casingFormat(practice.practiceName);
        }
      });
    });
  }

  const handleSearch = useCallback(
    (event) => {
      event.preventDefault();
      const searchText = event.target.elements.search.value.trim();

      if (searchText?.length < 3) {
        showInfoDialog({
          title: 'Error',
          message: 'Minimum 3 characters needed for search',
        });
        return;
      }

      dispatch(
        updateShipmentFilter({
          filter: { flatSearch: searchText },
        })
      );
    },
    [dispatch, showInfoDialog]
  );

  const removeFilter = useCallback(
    (filterName) => () => {
      const updates = { [filterName]: undefined };
      // If removing partner, also remove practice
      if (filterName === 'partnerIds') {
        updates.practiceIds = undefined;
      }
      // Remove both date range bounds together
      if (filterName === 'shipDateAfter') {
        updates.shipDateBefore = undefined;
      }
      if (filterName === 'shippedDateAfter') {
        updates.shippedDateBefore = undefined;
      }
      if (filterName === 'voidedDateAfter') {
        updates.voidedDateBefore = undefined;
      }
      dispatch(updateShipmentFilter({ filter: updates }));
    },
    [dispatch]
  );

  const removeAllFilters = useCallback(() => {
    dispatch(resetShipmentFilter());
  }, [dispatch]);

  const hasActiveFilters = !isEmpty(compact(values(filter)));

  return (
    <Box sx={{ px: 2, py: 1 }}>
      <InfoDialog />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {/* Filter status chips */}
        <Box sx={{ flexGrow: 1, minWidth: 0, mr: 'auto' }}>
          <Stack direction='row' flexWrap='wrap' alignItems='center' rowGap={0.5} spacing={0.5}>
            {filter.flatSearch && (
              <Chip
                size='small'
                label={`Search = ${filter.flatSearch}`}
                variant='contained'
                onDelete={removeFilter('flatSearch')}
              />
            )}

            {filter.recipientName && (
              <Chip
                size='small'
                label={`Name = ${filter.recipientName}`}
                variant='contained'
                onDelete={removeFilter('recipientName')}
              />
            )}

            {filter.referenceId && (
              <Chip
                size='small'
                label={`Order Id = ${filter.referenceId}`}
                variant='contained'
                onDelete={removeFilter('referenceId')}
              />
            )}

            {filter.trackingNumber && (
              <Chip
                size='small'
                label={`Tracking Number = ${filter.trackingNumber}`}
                variant='contained'
                onDelete={removeFilter('trackingNumber')}
              />
            )}

            <FacilityFilterStatusChip
              removeFilter={removeFilter}
              selectedFacilities={filter?.fulfillingPharmacyIds || []}
            />

            {filter?.partnerIds && (
              <Chip
                size='small'
                label={`Partner = ${partnerOptions
                  .filter((opt) => filter.partnerIds.includes(opt.value))
                  .map((opt) => opt.label)
                  .join(', ')}`}
                variant='contained'
                onDelete={removeFilter('partnerIds')}
              />
            )}

            {filter?.partnerIds && filter?.practiceIds && (
              <Chip
                size='small'
                label={`Practice = ${Object.values(practiceLabels).join(', ')}`}
                variant='contained'
                onDelete={removeFilter('practiceIds')}
              />
            )}

            {(filter?.shipDateAfter || filter?.shipDateBefore) && (
              <Chip
                size='small'
                label={`Ship Date = ${filter.shipDateAfter || '...'} - ${filter.shipDateBefore || '...'}`}
                variant='contained'
                onDelete={removeFilter('shipDateAfter')}
              />
            )}

            {(filter?.shippedDateAfter || filter?.shippedDateBefore) && (
              <Chip
                size='small'
                label={`Shipped Date = ${filter.shippedDateAfter || '...'} - ${filter.shippedDateBefore || '...'}`}
                variant='contained'
                onDelete={removeFilter('shippedDateAfter')}
              />
            )}

            {(filter?.voidedDateAfter || filter?.voidedDateBefore) && (
              <Chip
                size='small'
                label={`Voided Date = ${filter.voidedDateAfter || '...'} - ${filter.voidedDateBefore || '...'}`}
                variant='contained'
                onDelete={removeFilter('voidedDateAfter')}
              />
            )}

            {filter?.carrierServiceCodes && (
              <Chip
                size='small'
                label={`Shipping = ${filter.carrierServiceCodes.map((c) => formatEnumLabel(c)).join(', ')}`}
                variant='contained'
                onDelete={removeFilter('carrierServiceCodes')}
              />
            )}

            {filter?.isManualShipment && (
              <Chip
                size='small'
                label='Manual Shipments'
                variant='contained'
                onDelete={removeFilter('isManualShipment')}
              />
            )}

            {filter?.hasTags && (
              <Chip
                size='small'
                label='Has Tags'
                variant='contained'
                onDelete={removeFilter('hasTags')}
              />
            )}

            {filter?.tagLabels && (
              <Chip
                size='small'
                label={`Tags = ${filter.tagLabels.join(', ')}`}
                variant='contained'
                onDelete={removeFilter('tagLabels')}
              />
            )}

            {hasActiveFilters && (
              <Button size='small' onClick={removeAllFilters}>
                Remove All
              </Button>
            )}
            <Chip
              size='small'
              label={`Total: ${count}`}
              color='primary'
              sx={{ color: '#FFFFFF' }}
            />
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Paper
            component='form'
            sx={{ display: 'flex', alignItems: 'center', width: 280 }}
            onSubmit={handleSearch}
          >
            <InputBase
              inputProps={{
                autoComplete: 'off',
                'aria-label': 'search',
              }}
              sx={{ border: 'none', px: theme.spacing(2), flex: 1 }}
              type='text'
              placeholder='Search...'
              name='search'
              defaultValue={filter?.flatSearch || ''}
            />
            <IconButton type='submit' aria-label='search'>
              <Search />
            </IconButton>
          </Paper>
          <ShipmentsFilterButton />
          {createManualShipment && (
            <Button
              variant='contained'
              onClick={() => setShowCreateManualShipment(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Add Shipment
            </Button>
          )}
        </Box>
      </Box>

      {showCreateManualShipment && (
        <CreateManualShipmentDialog
          open={showCreateManualShipment}
          onClose={() => setShowCreateManualShipment(false)}
        />
      )}
    </Box>
  );
};

export default ShipmentListToolbar;
